import React from 'react';
import { Box, Card, CardContent, Typography, Chip, alpha } from '@mui/material';
import { useTranslation } from 'react-i18next';

const BACKTEST_DATA = {
  ONEIL: {
    trades: 49, winRate: 59.2, avgPnl: 1.58, pf: 1.58, cagr: 3.5,
    maxDD: -5.3, sharpe: 0.79, calmar: 0.66, costs: '58M',
    s80: { trades: 49, winRate: 59.2, avgPnl: 1.58, pf: 1.58, sharpe: 0.79 },
  },
  MINERVINI: {
    trades: 33, winRate: 42.4, avgPnl: -0.03, pf: 0.91, cagr: -0.6,
    maxDD: -10.6, sharpe: -0.14, calmar: -0.06, costs: '52M',
    s80: { trades: 13, winRate: 61.5, avgPnl: 3.41, pf: 2.51, sharpe: 1.03 },
  },
  WEINSTEIN: {
    trades: 98, winRate: 54.1, avgPnl: 0.34, pf: 1.01, cagr: 0.2,
    maxDD: -16.7, sharpe: 0.06, calmar: 0.01, costs: '133M',
    s80: { trades: 98, winRate: 54.1, avgPnl: 0.34, pf: 1.01, sharpe: 0.06 },
  },
  TURTLE: {
    trades: 229, winRate: 42.8, avgPnl: 0.06, pf: 1.05, cagr: 2.4,
    maxDD: -18.8, sharpe: 0.24, calmar: 0.13, costs: '399M',
    s80: { trades: 225, winRate: 44.0, avgPnl: 0.23, pf: 1.09, sharpe: 0.40 },
  },
  LIVERMORE: {
    trades: 166, winRate: 54.2, avgPnl: 0.87, pf: 1.25, cagr: 6.3,
    maxDD: -8.4, sharpe: 0.71, calmar: 0.75, costs: '215M',
    s80: { trades: 166, winRate: 54.2, avgPnl: 0.87, pf: 1.25, sharpe: 0.71 },
  },
};

const metricColor = (val, good, bad) => {
  if (val >= good) return '#10b981';
  if (val <= bad) return '#ef4444';
  return '#f59e0b';
};

function Metric({ label, value, suffix = '', good, bad, accent }) {
  const color = (good != null && bad != null)
    ? metricColor(parseFloat(value), good, bad)
    : accent || '#0f172a';

  return (
    <Box sx={{ textAlign: 'center', flex: 1, minWidth: 65 }}>
      <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{
        display: 'block', lineHeight: 1, mb: 0.4,
        fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: '0.04em',
      }}>
        {label}
      </Typography>
      <Typography fontWeight={800} sx={{ color, lineHeight: 1, fontSize: '0.95rem', fontFamily: 'monospace' }}>
        {value}{suffix}
      </Typography>
    </Box>
  );
}

export default function BacktestSummary({ strategy, showS80 = false, color = '#667eea' }) {
  const { t } = useTranslation();
  const data = BACKTEST_DATA[strategy?.toUpperCase()];
  if (!data) return null;

  const d = showS80 ? data.s80 : data;

  return (
    <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
      {/* Header */}
      <Box sx={{
        px: 2, py: 1.2,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        bgcolor: '#fafbfc', borderBottom: '1px solid', borderColor: 'divider',
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="subtitle2" fontWeight={700} sx={{ fontSize: '0.82rem' }}>
            Backtest 2023–2026
          </Typography>
          {showS80 && (
            <Chip label="Score ≥ 80" size="small" sx={{
              height: 18, fontSize: '0.62rem', fontWeight: 700,
              bgcolor: alpha(color, 0.08), color: color,
            }} />
          )}
        </Box>
        <Typography variant="caption" color="text.disabled" sx={{ fontSize: '0.62rem' }}>
          {t('legendary.backtestSummary.config', '1B VND · Slip 0.3% · Fee 0.15% · Max 5 positions')}
        </Typography>
      </Box>

      <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
        <Box sx={{ display: 'flex', gap: 0.3, flexWrap: 'wrap', justifyContent: 'space-between' }}>
          <Metric label="Trades" value={d.trades} accent="#64748b" />
          <Metric label="Win Rate" value={d.winRate?.toFixed?.(1) ?? d.winRate} suffix="%" good={55} bad={45} />
          <Metric label="Avg PnL"
            value={d.avgPnl > 0 ? `+${d.avgPnl?.toFixed?.(2) ?? d.avgPnl}` : d.avgPnl?.toFixed?.(2) ?? d.avgPnl}
            suffix="%" good={1} bad={0}
          />
          <Metric label="PF" value={d.pf?.toFixed?.(2) ?? d.pf} good={1.3} bad={1.0} />
          <Metric label="Sharpe" value={d.sharpe?.toFixed?.(2) ?? d.sharpe} good={0.5} bad={0} />
          {!showS80 && (
            <>
              <Metric label="CAGR"
                value={data.cagr > 0 ? `+${data.cagr}` : data.cagr}
                suffix="%" good={3} bad={0}
              />
              <Metric label="Max DD" value={data.maxDD} suffix="%" good={-10} bad={-20} />
              <Metric label="Calmar" value={data.calmar?.toFixed?.(2) ?? data.calmar} good={0.5} bad={0} />
            </>
          )}
        </Box>
      </CardContent>
    </Card>
  );
}

export { BACKTEST_DATA };
